import type { FC } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  X,
  Mail,
  FileText,
  Users,
  ShieldCheck,
} from 'lucide-react';
import type { CampaignValidationResult } from '@antigravity/types';

interface CampaignValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: CampaignValidationResult | null;
  campaignName: string;
}

export const CampaignValidationModal: FC<CampaignValidationModalProps> = ({
  isOpen,
  onClose,
  result,
  campaignName,
}) => {
  if (!isOpen || !result) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
      <div className="relative w-full max-w-xl rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl p-6 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            {result.valid ? (
              <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <CheckCircle2 className="w-5 h-5" />
              </div>
            ) : (
              <div className="p-2 rounded-xl bg-rose-500/10 text-rose-400 border border-rose-500/20">
                <AlertCircle className="w-5 h-5" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-slate-100">
                {result.valid
                  ? 'Campaign Ready for Dispatch'
                  : 'Campaign Validation Issues'}
              </h3>
              <p className="text-xs text-slate-400">
                Readiness check for "{campaignName}"
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-200 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content & Checklist */}
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800 flex items-center justify-between">
              <span className="flex items-center gap-2 text-slate-300">
                <FileText className="w-4 h-4 text-slate-400" />
                Subject Line
              </span>
              {result.summary.hasSubject ? (
                <span className="text-emerald-400 font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Valid
                </span>
              ) : (
                <span className="text-rose-400 font-medium flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Missing
                </span>
              )}
            </div>

            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800 flex items-center justify-between">
              <span className="flex items-center gap-2 text-slate-300">
                <Mail className="w-4 h-4 text-slate-400" />
                Body Content
              </span>
              {result.summary.hasContent ? (
                <span className="text-emerald-400 font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Valid
                </span>
              ) : (
                <span className="text-rose-400 font-medium flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Missing
                </span>
              )}
            </div>

            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800 flex items-center justify-between">
              <span className="flex items-center gap-2 text-slate-300">
                <ShieldCheck className="w-4 h-4 text-slate-400" />
                Sender & Domain
              </span>
              {result.summary.senderEligible && result.summary.domainVerified ? (
                <span className="text-emerald-400 font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                </span>
              ) : (
                <span className="text-rose-400 font-medium flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Ineligible
                </span>
              )}
            </div>

            <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-800 flex items-center justify-between">
              <span className="flex items-center gap-2 text-slate-300">
                <Users className="w-4 h-4 text-slate-400" />
                Recipients
              </span>
              <span className="text-slate-200 font-semibold">
                {result.summary.recipientCount} recipient{result.summary.recipientCount === 1 ? '' : 's'}
              </span>
            </div>
          </div>

          {/* Validation Errors */}
          {result.errors.length > 0 && (
            <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-200">
              <div className="text-xs font-semibold mb-2 flex items-center gap-2 text-rose-300">
                <AlertCircle className="w-4 h-4" />
                Action Required ({result.errors.length} error{result.errors.length === 1 ? '' : 's'}):
              </div>
              <ul className="list-disc list-inside space-y-1 text-xs text-rose-200/90 pl-1">
                {result.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Validation Warnings */}
          {result.warnings.length > 0 && (
            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-200">
              <div className="text-xs font-semibold mb-1 flex items-center gap-2 text-amber-300">
                <AlertTriangle className="w-4 h-4" />
                Notice:
              </div>
              <ul className="list-disc list-inside space-y-1 text-xs text-amber-200/90 pl-1">
                {result.warnings.map((warn, idx) => (
                  <li key={idx}>{warn}</li>
                ))}
              </ul>
            </div>
          )}

          {result.valid && (
            <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-200 text-xs">
              All pre-flight checks passed successfully! The campaign meets all tenant authorization,
              domain verification, content sanitization, and recipient list standards.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
